import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Lock, MessageSquareWarning, ShieldAlert, HeartPulse, Scale } from "lucide-react";
import { Overline } from "@/components/shared";
import { SystemStrip, SCENARIO_GUIDE_URL } from "@/components/SystemStrip";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const INCLUDES = [
  "Difficult conversations",
  "Conflict on the team",
  "Emotional and high-pressure situations",
  "Boundaries and expectations",
  "Underperformance and workload concerns",
  "Change, uncertainty, and ambiguity",
];

const MOMENTS_OUTLINE = [
  { section: "Performance & Accountability", items: ["Establish regular 1:1s", "Address a performance issue", "Underperforming but trying", "Hold someone accountable", "Expectations keep being missed", "Deliver hard feedback", "When someone becomes defensive", "Improves… then slips again"] },
  { section: "Emotional & Human Moments", items: ["Big life events on your team", "Someone is crying at work", "Team morale is low", "Someone seems disengaged", "Grief enters the workplace", "Personal conflict spills into work"] },
  { section: "Identity, Authority & Leadership", items: ["Being effective vs. being liked", "Not empowered in your role", "Feeling like an imposter", "Setting boundaries", "Managing someone more experienced"] },
  { section: "Team Dynamics & Relationships", items: ["Peer to boss transition", "Rebuilding trust", "One person dominates", "Someone feels left out", "Inheriting a team", "Gossip or tension", "Passive-aggressive behavior", "and more"] },
  { section: "Career & Transitions", items: ["Someone resigns", "A promotion you can't give", "Preparing for their next role", "A new employee joins", "Returning from leave"] },
  { section: "Self-Management & Managing Up", items: ["Feeling lonely or burned out", "Dreading logging in", "Recovering after hard talks", "Navigating change & ambiguity", "Doing more with less", "Priorities keep changing"] },
];

export default function Moments() {
  const { setPart2 } = useTheme();
  const { entitlements } = useAuth();
  const owned = entitlements.has_part_3 || entitlements.bundle_owner;
  useEffect(() => { setPart2(false); return () => setPart2(false); }, [setPart2]);

  return (
    <div className="max-w-3xl" data-testid="moments-page">
      <SystemStrip current="moments" />

      <Overline className="text-nm-coral">Toolkit 3 · The Scenario Guide</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-nm-ink mt-2 leading-tight" data-testid="moments-title">The Moments</h1>
      <p className="font-serif italic text-2xl text-nm-primary mt-3">Handle real situations.</p>

      <p className="text-lg text-nm-neutral font-light leading-relaxed mt-6">
        The final part of your Manager Operating System is about the moments no training fully prepares you for —
        the real situations managers face once the plans are made and the work is underway.
      </p>
      <p className="text-nm-ink/85 leading-relaxed text-[17px] mt-4">
        Toolkits 1 and 2 build the foundation: understanding the role and leading the people. The Scenario Guide
        picks up where they leave off, giving you practical, situation-by-situation guidance — and ready-to-use
        scripts — for the complicated, uncomfortable, and high-pressure moments that actually determine how
        management feels day to day.
      </p>

      <div className="mt-8 rounded-3xl border border-nm-coral/25 bg-nm-coral/[0.05] p-7" data-testid="moments-includes">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-nm-coral">What the Scenario Guide covers</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {INCLUDES.map((it, i) => (
            <div key={i} className="flex items-start gap-3 text-nm-ink/85 leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-nm-coral shrink-0" />{it}
            </div>
          ))}
        </div>
        <p className="text-nm-neutral text-[15px] leading-relaxed mt-5">
          Each scenario comes with guidance on how to think about the moment and language you can adapt and use in real time.
        </p>
      </div>

      {owned ? (
        <>
          <a
            href={SCENARIO_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-scenario-guide-btn"
            className="mt-9 inline-flex items-center gap-2 rounded-full bg-nm-primary px-7 py-3.5 text-white font-medium hover:brightness-105 transition-[filter] duration-200"
          >
            Open the Scenario Guide <ArrowUpRight size={18} />
          </a>
          <p className="text-[13px] text-nm-neutral mt-3">Opens the Manager Toolkit: The Scenario Guide in a new tab.</p>
        </>
      ) : (
        <div className="mt-9 rounded-3xl border border-nm-primary/15 bg-nm-primary/[0.04] p-7" data-testid="moments-locked">
          <div className="flex items-center gap-2 text-nm-primary font-medium text-sm"><Lock size={16} /> Locked toolkit</div>
          <p className="text-nm-ink/85 leading-relaxed mt-3">
            Unlock Toolkit 3 to open the full Scenario Guide — situation-by-situation guidance and ready-to-use scripts for the hardest moments in management.
          </p>
          <Link
            to="/app/account?intent=part_3#upgrade"
            data-testid="moments-buy-btn"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-nm-primary px-7 py-3.5 text-white font-medium hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200"
          >
            Get The Moments — $65 <ArrowRight size={18} />
          </Link>
          <p className="text-[13px] text-nm-neutral mt-3">Or get all three toolkits together for $170.</p>
        </div>
      )}

      {!owned && (
        <div className="mt-8" data-testid="moments-content-summary">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B0851E]">A look inside · 45+ scenarios</div>
          <h2 className="font-serif text-2xl text-nm-ink mt-2">The Content</h2>
          <div className="mt-5 columns-1 sm:columns-2 gap-4 [column-fill:balance]">
            {MOMENTS_OUTLINE.map((sec) => (
              <div key={sec.section} className="mb-4 break-inside-avoid rounded-xl bg-white border border-nm-ink/[0.06] p-4 nm-shadow-sm">
                <div className="font-serif text-[15px] font-semibold leading-snug text-[#B0851E]">{sec.section}</div>
                <ul className="mt-2 space-y-1">
                  {sec.items.map((it) => (
                    <li key={it} className="flex gap-2 text-[13px] leading-snug text-nm-ink/80">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-[#C2952B] shrink-0" />{it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-[13px] font-serif italic font-semibold mt-1 text-[#B0851E]">and more coming…</p>
        </div>
      )}

      <div className="mt-14 flex items-center gap-3 rounded-2xl border border-nm-primary/12 bg-nm-primary/[0.04] px-6 py-5">
        <span className="text-nm-ink/85 leading-relaxed">
          <span className="font-medium text-nm-ink">Three standalone toolkits. One connected system.</span>{" "}
          Understand the role. Lead the people. Navigate the moments.
        </span>
      </div>
    </div>
  );
}
